/**
 * Reports the product cards inside it to the data layer as a `dl_view_item_list`
 * list — each card once, as it comes into view.
 *
 * Cards opt in by carrying their own impression data (`track_impressions` on
 * the `product-card` snippet); this element owns what the list is: the
 * `position` a card holds in it, and the `list` and `category` it reports under.
 *
 * `ready_to_ship`, `final_sale` and `badge` are deliberately absent — Elevar's
 * transform scrapes those off the cards themselves as the event is pushed.
 */

// Long enough for the cards that come into view together — a panel opening, a
// row scrolled to — to go over as one event.
const BATCH_DELAY = 250;

const parseImpression = (card) => {
  try {
    return JSON.parse(card.dataset.impression);
  } catch (error) {
    return null;
  }
};

const formatPrice = (cents) => (cents / 100).toFixed(2);

class ProductCardImpressions extends HTMLElement {
  connectedCallback() {
    this.tracked = new Map();
    this.pending = [];

    this.observer = new IntersectionObserver((entries) => this._seen(entries));
    // Direct children only — a card reports the view it was rendered for, so a
    // swatch re-rendering one from the inside isn't worth waking for. A list
    // that fills a nested container instead would need `subtree`.
    this.mutations = new MutationObserver(() => this._sync());
    this.mutations.observe(this, { childList: true });

    this._sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.mutations?.disconnect();
    clearTimeout(this.batch);
  }

  /** Keeps up with a list filled, swapped or emptied after it is rendered. */
  _sync() {
    const cards = this.cards;

    this.tracked.forEach((impression, card) => {
      if (cards.includes(card)) return;

      this.observer.unobserve(card);
      this.tracked.delete(card);
    });

    cards.forEach((card) => {
      // Tracked covers reported cards too — those are unobserved but kept, so
      // they aren't picked back up here.
      if (this.tracked.has(card)) return;

      const impression = parseImpression(card);

      if (!impression) return;

      // Held from first sight: selecting a swatch re-renders the card over its
      // own markup, taking the data attribute with it.
      this.tracked.set(card, impression);
      this.observer.observe(card);
    });
  }

  _seen(entries) {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return;

      this.observer.unobserve(target);
      this.pending.push(target);
    });

    if (!this.pending.length) return;

    clearTimeout(this.batch);
    this.batch = setTimeout(() => this._report(), BATCH_DELAY);
  }

  _report() {
    const cards = this.cards;
    const impressions = this.pending
      // A card taken out of the list while the batch gathered never made it
      // into view for long enough to count.
      .filter((card) => this.tracked.has(card))
      .map((card) => {
        const impression = this.tracked.get(card);

        return {
          ...impression,
          category: this.category || impression.category,
          price: formatPrice(impression.price),
          compare_at_price: formatPrice(impression.compare_at_price),
          position: cards.indexOf(card) + 1,
          list: this.list,
        };
      });

    this.pending = [];

    if (!impressions.length) return;

    window.ElevarDataLayer?.push({
      event: "dl_view_item_list",
      ecommerce: {
        currencyCode: window.Shopify.currency.active,
        impressions,
      },
    });
  }

  get cards() {
    return Array.from(this.querySelectorAll("product-card"));
  }

  get category() {
    return this.getAttribute("category");
  }

  /** The page the list is being viewed on, matching Elevar's own lists. */
  get list() {
    return this.getAttribute("list") || window.location.pathname;
  }
}

if (!customElements.get("product-card-impressions")) {
  customElements.define("product-card-impressions", ProductCardImpressions);
}
